import React, { useState } from "react";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import toast, { Toaster } from "react-hot-toast";
import { landingApis } from "../../api/landingApi";
import "../../style/ContactUs.css";
import merubotLogo from "../../assets/images/merubot_logo.png";

const COUNTRY_OPTIONS = ["India", "United States", "United Kingdom", "UAE", "Singapore", "Australia", "Canada", "Other"];

const JOB_TITLES = [
  "HR Manager",
  "Talent Acquisition Specialist",
  "Recruiter",
  "Hiring Manager",
  "Founder / CEO",
  "CTO / Tech Lead",
  "Operations Manager",
  "Other",
];

const COUNTRY_CODES = [
  { code: "+91",  label: "🇮🇳 +91",  digits: 10 },
  { code: "+1",   label: "🇺🇸 +1",   digits: 10 },
  { code: "+44",  label: "🇬🇧 +44",  digits: 10 },
  { code: "+61",  label: "🇦🇺 +61",  digits: 9  },
  { code: "+971", label: "🇦🇪 +971", digits: 9  },
  { code: "+65",  label: "🇸🇬 +65",  digits: 8  },
  { code: "+60",  label: "🇲🇾 +60",  digits: 9  },
  { code: "+49",  label: "🇩🇪 +49",  digits: 10 },
  { code: "+33",  label: "🇫🇷 +33",  digits: 9  },
  { code: "+81",  label: "🇯🇵 +81",  digits: 10 },
];

const INITIAL_FORM = {
  name: "",
  email: "",
  countryCode: "+91",
  phone: "",
  companyName: "",
  jobTitle: "",
  country: "India",
  helpMessage: "",
};

export default function ContactUs() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);


  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) nextErrors.name = "Name is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) nextErrors.email = "Valid business email is required";
    if (!form.phone.trim()) {
  nextErrors.phone = "Phone number is required";
} else {
  const selected = COUNTRY_CODES.find(c => c.code === form.countryCode);
  const requiredDigits = selected?.digits || 10;
  if (!/^\d+$/.test(form.phone)) {
    nextErrors.phone = "Phone number must contain only digits";
  } else if (form.phone.length !== requiredDigits) {
    nextErrors.phone = `Phone number must be exactly ${requiredDigits} digits for ${form.countryCode}`;
  }
}
    if (!form.companyName.trim()) nextErrors.companyName = "Company name is required";
    if (!form.jobTitle.trim()) nextErrors.jobTitle = "Please select your job title";
    if (!form.country.trim()) nextErrors.country = "Please select your country";
    if (!form.helpMessage.trim()) nextErrors.helpMessage = "Please tell us how we can help";
    if (form.helpMessage.trim().length > 250) nextErrors.helpMessage = "Message must be 250 characters or less";
    if (!captchaToken) nextErrors.captcha = "Please verify that you are not a robot";

    return nextErrors;
  };

  const handleCountryCodeChange = (e) => {
  setForm(prev => ({ ...prev, countryCode: e.target.value, phone: "" }));
  if (errors.phone) setErrors(prev => ({ ...prev, phone: "" }));
};

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);

    try {
      const response = await landingApis.requestContact({
        ...form,
        captchaToken,
      });

      const message = response?.message || response?.data?.message;

      if (response?.status >= 400) {
        toast.error(message || "Unable to submit your request");
        return;
      }

      setSubmitted(true);
      toast.success(message || "Contact request submitted successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            fontFamily: "'DM Sans', sans-serif",
            borderRadius: "12px",
            background: "#fff",
            color: "#0f2535",
            boxShadow: "0 8px 32px rgba(26,120,160,0.15)",
          },
        }}
      />

      <div className="contact-page__shell">
        <header className="contact-topbar">
          <a className="contact-topbar__brand" href="/">
            <img src={merubotLogo} alt="MeruBot" />
          </a>
          <a className="contact-topbar__back" href="/">
            Back to Home
          </a>
        </header>

        <section className="contact-hero">
          <div className="contact-hero__content">
            <h1>
              Let&apos;s build your
              <span> smarter hiring workflow.</span>
            </h1>
            <p>
              Share a few details and our team will reach out with the right MeruBot walkthrough for your hiring process.
            </p>
            <ul className="contact-hero__highlights">
              {/* <li>Personalized product guidance</li>
              <li>Enterprise-ready onboarding support</li>
              <li>Response typically within 1 business day</li> */}
            </ul>
          </div>
        </section>

        <section className="contact-form-card">
          {!submitted ? (
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-grid">
                <Field label="Name" name="name" placeholder="What do we call you?" value={form.name} onChange={handleChange} error={errors.name} required />
                <Field label="Email address" name="email" type="email" placeholder="Please enter your business email ID" value={form.email} onChange={handleChange} error={errors.email} required />
                <div className={`contact-field ${errors.phone ? "contact-field--error" : ""}`}>
  <label htmlFor="phone">Phone <span>*</span></label>
  <div className="phone-input-row">
    <select
      name="countryCode"
      value={form.countryCode}
      onChange={handleCountryCodeChange}
      className="country-code-select"
    >
      {COUNTRY_CODES.map(c => (
        <option key={c.code} value={c.code}>{c.label}</option>
      ))}
    </select>
    <input
      id="phone"
      type="tel"
      name="phone"
      placeholder="9876543210"
      value={form.phone}
      onChange={handleChange}
      maxLength={COUNTRY_CODES.find(c => c.code === form.countryCode)?.digits || 10}
    />
  </div>
  {errors.phone && <span className="contact-field__error">{errors.phone}</span>}
</div>
                <Field label="Company Name" name="companyName" placeholder="Please enter your company name" value={form.companyName} onChange={handleChange} error={errors.companyName} required />
                <SelectField label="Job Title" name="jobTitle" value={form.jobTitle} onChange={handleChange} error={errors.jobTitle} required options={JOB_TITLES} placeholder="Select Your Job Title" />
                <SelectField label="Country" name="country" value={form.country} onChange={handleChange} error={errors.country} required options={COUNTRY_OPTIONS} />
                <TextAreaField
                  label="How can our team help you?"
                  name="helpMessage"
                  placeholder="Do you have any additional specifications for your hiring workflow? (max. 250 characters)"
                  value={form.helpMessage}
                  onChange={handleChange}
                  error={errors.helpMessage}
                  required
                />
              </div>

              <div className="contact-form__captcha">
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}>
                  <div style={{
                    transform: "scale(0.78)",
                    transformOrigin: "left top",
                    marginBottom: "-14px",
                  }}>
                    <ReCAPTCHA
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "your_placeholder_site_key_here"}
                      onChange={(token) => {
                        setCaptchaToken(token);
                        if (errors.captcha) {
                          setErrors((prev) => ({ ...prev, captcha: "" }));
                        }
                      }}
                    />
                  </div>
                </div>
                {errors.captcha && <span className="contact-field__error" style={{ display: "block", textAlign: "center" }}>{errors.captcha}</span>}
              </div>

              <p className="contact-form__disclaimer">
                By submitting this form, you agree to the processing of personal data according to our{" "}
                <Link to="/privacy-policy?tab=privacy&section=scope">Privacy Policy</Link>.
              </p>

              <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={submitting || !captchaToken}
                  style={{ width: "fit-content", padding: "10px 32px", fontSize: "14px" }}
                >
                  {submitting ? "Submitting…" : "Submit"}
                </button>
              </div>
            </form>
          ) : (
            <div className="contact-success">
              <div className="contact-success__badge">Request Received</div>
              <h2>Thanks for reaching out.</h2>
              <p>
                Our team has your request and will contact you shortly from <strong>notification@merubot.com</strong>.
              </p>
              <a className="contact-success__link" href="/">
                Return to Home
              </a>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, name, type = "text", placeholder, value, onChange, error, required }) {
  return (
    <div className={`contact-field ${error ? "contact-field--error" : ""}`}>
      <label htmlFor={name}>
        {label}
        {required ? <span>*</span> : null}
      </label>
      <input id={name} name={name} type={type} placeholder={placeholder} value={value} onChange={onChange} />
      {error ? <span className="contact-field__error">{error}</span> : null}
    </div>
  );
}

function SelectField({ label, name, value, onChange, error, required, options, placeholder }) {
  return (
    <div className={`contact-field ${error ? "contact-field--error" : ""}`}>
      <label htmlFor={name}>
        {label}
        {required ? <span>*</span> : null}
      </label>
      <select id={name} name={name} value={value} onChange={onChange}>
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error ? <span className="contact-field__error">{error}</span> : null}
    </div>
  );
}

function TextAreaField({ label, name, placeholder, value, onChange, error, required }) {
  return (
    <div className={`contact-field contact-field--full ${error ? "contact-field--error" : ""}`}>
      <label htmlFor={name}>
        {label}
        {required ? <span>*</span> : null}
      </label>
      <textarea id={name} name={name} placeholder={placeholder} value={value} onChange={onChange} rows={5} maxLength={250} />
      <div className="contact-field__meta">
        <span>{value.length}/250</span>
      </div>
      {error ? <span className="contact-field__error">{error}</span> : null}
    </div>
  );
}
